import { useCallback, useMemo } from "react";

import type { BillingTypeTarget, BillingTypeTerm, CurrencyType } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";
import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

type ViewModel = {
	billingTypeTarget: ForNodeType<BillingTypeTarget>;
	billingTypeTerm: ForNodeType<BillingTypeTerm>;
	price: ForAutoGenerator<
		{
			currencyType: CurrencyType;
			amount?: number | undefined;
		},
		""
	>;
};

type Props = {
	billingTypeTarget: ViewModel["billingTypeTarget"];
	billingTypeTerm: ViewModel["billingTypeTerm"];
	price: ViewModel["price"];

	onSetViewModel: <K extends keyof ViewModel>(values: { key: K; value: ViewModel[K] }[]) => void;
};

export const usePlanPrice = ({ billingTypeTarget, billingTypeTerm, price, onSetViewModel }: Props) => {
	const onSetBillingTypeTerm = useCallback(
		(input: BillingTypeTerm) => {
			onSetViewModel([
				{
					key: "billingTypeTerm",
					value: {
						...billingTypeTerm,
						value: input,
					},
				},
			]);
		},
		[billingTypeTerm, onSetViewModel],
	);

	const onSetBillingTypeTarget = useCallback(
		(input: BillingTypeTarget) => {
			onSetViewModel([
				{
					key: "billingTypeTarget",
					value: {
						...billingTypeTarget,
						value: input,
					},
				},
			]);
		},
		[billingTypeTarget, onSetViewModel],
	);

	const onSetPriceAmount = useCallback(
		(input: number | undefined) => {
			onSetViewModel([{ key: "price", value: { ...price, amount: { isEdited: true, value: input } } }]);
		},
		[price, onSetViewModel],
	);

	const isEdited = useMemo(
		() => price.amount?.isEdited ?? billingTypeTarget.isEdited ?? billingTypeTerm.isEdited ?? false,
		[billingTypeTarget.isEdited, billingTypeTerm.isEdited, price.amount?.isEdited],
	);
	const hasError = useMemo(
		() => Boolean(price.amount?.error || billingTypeTarget?.error || billingTypeTerm?.error),
		[billingTypeTarget?.error, billingTypeTerm?.error, price.amount?.error],
	);

	return {
		isEdited,
		hasError,
		billingTypeTerm,
		onSetBillingTypeTerm,
		billingTypeTarget,
		onSetBillingTypeTarget,
		price,
		onSetPriceAmount,
	};
};
